import { useMemo, useState } from 'react'
import { Button, Form, Modal } from 'react-bootstrap'
import { jsPDF } from 'jspdf'
import UbuntuBase64 from '../assets/fonts/UbuntuBase64'
import UbuntuBoldBase64 from '../assets/fonts/UbuntuBoldBase64'
import { fetchStoragePrintData } from './storagePrintData'

const toTrim = (value) => String(value ?? '').trim()
const toNum = (value, fallback = 0) => {
  const n = Number(value)
  return Number.isFinite(n) ? n : fallback
}

const toBase64Payload = (asset) => {
  const str = String(asset || '')
  const idx = str.indexOf(',')
  return idx >= 0 ? str.slice(idx + 1) : str
}

const normalizeText = (value) =>
  toTrim(value)
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const normalizeBool = (value) => {
  if (typeof value === 'boolean') return value
  if (value === 1 || value === '1') return true
  const text = normalizeText(value)
  return text === 'true' || text === 'ano' || text === 'yes'
}

const seasonLabel = (value) => {
  const text = normalizeText(value)
  if (text === '1' || text === 'letni' || text === 'leto') return 'letní'
  if (text === '2' || text === 'zimni' || text === 'zima') return 'zimní'
  if (text === '3' || text === 'celorocni') return 'celoroční'
  return toTrim(value) || '-'
}

const diskLabel = (value) => {
  const text = normalizeText(value)
  if (text === '0' || text === 'bez' || text === 'bez disku') return 'bez disku'
  if (text === '1' || text === 'ocel' || text === 'ocelovy') return 'ocelový disk'
  if (text === '2' || text === 'hlinik' || text === 'hlinikovy') return 'hliníkový disk'
  return toTrim(value) || 'bez disku'
}

const registerUbuntuFonts = (doc) => {
  doc.addFileToVFS('Ubuntu-Regular.ttf', toBase64Payload(UbuntuBase64))
  doc.addFont('Ubuntu-Regular.ttf', 'Ubuntu', 'normal')
  doc.addFileToVFS('Ubuntu-Bold.ttf', toBase64Payload(UbuntuBoldBase64))
  doc.addFont('Ubuntu-Bold.ttf', 'Ubuntu', 'bold')
}

const drawCheckbox = (doc, x, y, checked) => {
  const size = 3.4
  doc.setDrawColor(0)
  doc.setLineWidth(0.25)
  doc.rect(x, y, size, size)
  if (!checked) return
  doc.setLineWidth(0.4)
  doc.line(x + 0.6, y + 1.8, x + 1.4, y + 2.6)
  doc.line(x + 1.4, y + 2.6, x + 2.8, y + 0.8)
}

const drawLabel = (doc, label, x, y, w, h) => {
  const innerPad = 12
  const innerLeft = x + innerPad
  const innerRight = x + w - innerPad
  const contentHeight = 54
  const contentTop = y + ((h - contentHeight) / 2)
  const contentBottom = contentTop + contentHeight
  const spz = toTrim(label.spz) || '-'
  const cisloZakazky = toTrim(label.cisloZakazky) || '-'
  const firma = toTrim(label.firma)
  const jmeno = toTrim(label.jmeno)
  const rozmer = toTrim(label.rozmer)
  const znacka = toTrim(label.znacka)
  const dezen = toTrim(label.dezen)
  const sezona = seasonLabel(label.sezona)
  const disk = diskLabel(label.disk)
  const tmps = normalizeBool(label.tmps)
  const poklice = normalizeBool(label.poklice)
  const srouby = normalizeBool(label.srouby)
  const rowMain = [rozmer, znacka, dezen].filter(Boolean).join(' ') || '-'

  // Rámeček štítku je dočasně vypnutý pro tisk bez orámování.
  // Pro opětovné zapnutí stačí odkomentovat následující 3 řádky.
  // doc.setDrawColor(0)
  // doc.setLineWidth(0.3)
  // doc.rect(x, y, w, h)

  doc.setFont('Ubuntu', 'bold')
  doc.setFontSize(32)
  doc.text(spz, innerLeft, contentTop + 8)

  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(16)
  doc.text(cisloZakazky, innerRight, contentTop + 4, { align: 'right' })

  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(14)
  doc.text((firma || '-').toUpperCase(), innerLeft, contentTop + 21)

  doc.setFontSize(14)
  doc.text(jmeno || '-', innerLeft, contentTop + 29)

  doc.setDrawColor(0)
  doc.setLineWidth(0.25)
  doc.line(innerLeft, contentTop + 35, innerRight, contentTop + 35)

  doc.setFontSize(9)
  const specLines = doc.splitTextToSize(rowMain, w - (innerPad * 2))
  doc.text(specLines.slice(0, 2), innerLeft, contentTop + 43)

  const seasonRowY = contentBottom - 6
  const tableX = innerLeft
  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(8.2)
  doc.setTextColor(0)
  doc.text(sezona, tableX, seasonRowY)
  doc.text(disk, tableX + 30, seasonRowY)

  const checkboxRowY = contentBottom - 0.5
  const colTmpsStart = tableX
  const colPokliceStart = tableX + 30
  const colSroubyStart = tableX + 62
  const checkboxY = checkboxRowY - 2.9
  const textY = checkboxRowY 
  drawCheckbox(doc, colTmpsStart, checkboxY, tmps)
  drawCheckbox(doc, colPokliceStart, checkboxY, poklice)
  drawCheckbox(doc, colSroubyStart, checkboxY, srouby)

  doc.setFont('Ubuntu', 'normal')
  doc.setFontSize(8.2)
  doc.text('tmps', colTmpsStart + 4.6, textY)
  doc.text('poklice', colPokliceStart + 4.6, textY)
  doc.text('šrouby', colSroubyStart + 4.6, textY)
}

const buildPdf = (label, labelCount, startPosition) => {
  const count = Math.max(1, Number(labelCount) || 1)
  const startSlot = Math.min(8, Math.max(1, Number(startPosition) || 1)) - 1
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
  registerUbuntuFonts(doc)

  const pageWidth = doc.internal.pageSize.getWidth()
  const pageHeight = doc.internal.pageSize.getHeight()
  const contentWidth = pageWidth
  const contentHeight = pageHeight
  const cols = 2
  const rows = 4
  const labelWidth = contentWidth / cols
  const labelHeight = contentHeight / rows
  const labelsPerPage = cols * rows

  for (let i = 0; i < count; i += 1) {
    const sheetIndex = startSlot + i
    const pageIndex = Math.floor(sheetIndex / labelsPerPage)
    if (pageIndex > 0 && sheetIndex % labelsPerPage === 0) doc.addPage()
    const position = sheetIndex % labelsPerPage
    const col = position % cols
    const row = Math.floor(position / cols)
    const x = col * labelWidth
    const y = row * labelHeight
    drawLabel(doc, label, x, y, labelWidth, labelHeight)
  }

  // Doporučení pro PDF viewer: tisk bez automatického škálování stránky.
  if (typeof doc.viewerPreferences === 'function') {
    doc.viewerPreferences({ PrintScaling: 'None' })
  }
  doc.autoPrint()
  const blobUrl = doc.output('bloburl')
  if (typeof window !== 'undefined') window.open(blobUrl, '_blank', 'noopener,noreferrer')
}

const StorageLabelsPrintTrigger = ({ zakazkaId, copies = 8, labelCount, renderButton, disabled = false }) => {
  const [showModal, setShowModal] = useState(false)
  const [startPosition, setStartPosition] = useState(1)
  const [isLoadingData, setIsLoadingData] = useState(false)
  const [printData, setPrintData] = useState(null)
  const [loadError, setLoadError] = useState('')

  const isReady = useMemo(() => toNum(zakazkaId, 0) > 0, [zakazkaId])

  const effectiveLabelCount = useMemo(() => {
    const fromProp = toNum(labelCount, 0)
    if (fromProp > 0) return fromProp
    const fromData = toNum(printData?.labelCount, 0)
    if (fromData > 0) return fromData
    return Math.max(1, toNum(copies, 8))
  }, [labelCount, printData?.labelCount, copies])

  const handleOpen = async (event) => {
    event?.stopPropagation?.()
    if (disabled || !isReady) return
    setShowModal(true)
    setLoadError('')
    setIsLoadingData(true)
    try {
      const loaded = await fetchStoragePrintData(zakazkaId)
      setPrintData(loaded)
    } catch (error) {
      setPrintData(null)
      setLoadError(error?.message || 'Nepodařilo se načíst data zakázky pro tisk štítků.')
    } finally {
      setIsLoadingData(false)
    }
  }

  const handleModalClose = () => {
    setShowModal(false)
    setLoadError('')
  }

  const handlePrint = () => {
    if (!printData || isLoadingData) return
    buildPdf(printData, effectiveLabelCount, startPosition)
    setShowModal(false)
  }

  const isPrintDisabled = disabled || !isReady || isLoadingData || !printData || !!loadError

  return (
    <>
      {typeof renderButton === 'function'
        ? renderButton({ onClick: handleOpen, disabled: disabled || !isReady })
        : (
          <button type="button" onClick={handleOpen} disabled={disabled || !isReady}>
            Tisk štítků
          </button>
        )}

      <Modal show={showModal} fullscreen="xl-down" onHide={handleModalClose} keyboard={true} centered size="sm">
        <Modal.Header closeButton className="border-0 p-4 pb-0">
          <Modal.Title className="text-primary lh-1">
            Tisk<br />
            <small className="text-muted fs-6">tisk štítků</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="small text-muted mb-3 px-2">
            Počet štítků k tisku: <span className="fs-5 fw-semibold">{effectiveLabelCount}</span>
          </p>
          {isLoadingData && <p className="small text-muted px-2 mb-2">Načítám data zakázky...</p>}
          {loadError && <p className="small text-danger px-2 mb-2">{loadError}</p>}
          <Form.Group>
            <Form.Label className="small text-muted mb-2 px-2">vyber pozici prvního štítku na archu:</Form.Label>
            <div
              className="border rounded p-4 mx-2"
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gridTemplateRows: 'repeat(4, 1fr)',
                gap: '3px',
                backgroundColor: 'var(--bs-tertiary-bg)',
              }}
            >
              {Array.from({ length: 8 }, (_, i) => i + 1).map((pos) => {
                const checked = startPosition === pos
                return (
                  <label
                    key={`pos-${pos}`}
                    className={`border rounded px-2 py-2 d-flex align-items-center justify-content-between ${checked ? 'border-primary bg-primary bg-opacity-10' : 'bg-body'}`}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    <span className="small text-muted">Pozice {pos}</span>
                    <Form.Check
                      type="radio"
                      name="label-start-position"
                      checked={checked}
                      onChange={() => setStartPosition(pos)}
                      className="mb-0"
                    />
                  </label>
                )
              })}
            </div>
          </Form.Group>
          <div className='mt-4 d-flex gap-1'>
           <Button className="ms-2" variant="primary" onClick={handlePrint} disabled={isPrintDisabled}>
            {isLoadingData ? 'Načítám...' : 'Tisk'}
          </Button>
          <Button className="ms-0" variant="secondary" onClick={handleModalClose}>Zavřít</Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  )
}

export default StorageLabelsPrintTrigger
